import ExportButton from '@/components/Chart/ExportButton';
import { Permission } from '@/entities/permissions';
import type { SymbolKey } from '@/entities/symbol/types';
import { requestSpotSelection } from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { ExportSelectionSource } from '@/hooks/useOpenExportSelection';
import {
  collectOtherGroupSymbols,
  hasOtherGroupSymbols
} from '@/pages/Markets/MarketsComponents/CommoditiesTable/useOtherGroupData';
import { getAllMiniTableSymbols } from '@/pages/Markets/MarketsComponents/MiniTablesContainer/utils/getMiniTableSymbols';
import { getSymbolsFromRows } from '@/pages/Markets/MarketsComponents/utils/getSymbolsFromRows';
import usePermission from '@/shared/hooks/usePermission';
import { openExportDrawer } from '@/slices/drawerSlice';
import { setExport } from '@/slices/exportSlice';
import type {
  BaseTabType,
  MarketsGraphType,
  MarketsMinitableType,
  MarketsTableType
} from '@/types/markets';

function getSymbolsFromTable(tab: MarketsTableType): SymbolKey[] {
  return tab.sections.flatMap(section => getSymbolsFromRows(section.rows));
}
const isExportSupportedTab = (
  tab: BaseTabType
): tab is MarketsTableType | MarketsMinitableType | MarketsGraphType =>
  tab.type === 'table' || tab.type === 'minitable';

const ExportWidgetButton = ({ tab }: { tab: BaseTabType }) => {
  const { hasPermission } = usePermission();

  const isPermissionGranted = hasPermission(Permission.ExportView);

  const dispatch = useAppDispatch();

  const handleClick = () => {
    switch (tab.type) {
      case 'table': {
        if (hasOtherGroupSymbols(tab.sections)) {
          dispatch(setExport({ exportNameBase: tab.name }));
          dispatch(
            openExportDrawer({
              showHistory: false,
              source: ExportSelectionSource.WIDGET_BUTTON,
              restrictedTableExportContext: {
                tabName: tab.name,
                symbols: collectOtherGroupSymbols(tab.sections)
              }
            })
          );
        } else {
          dispatch(requestSpotSelection({ symbols: getSymbolsFromTable(tab) }));
          dispatch(setExport({ exportNameBase: tab.name }));
          dispatch(
            openExportDrawer({ showHistory: false, source: ExportSelectionSource.WIDGET_BUTTON })
          );
        }
        break;
      }
      case 'minitable': {
        dispatch(requestSpotSelection({ symbols: getAllMiniTableSymbols(tab.tables) }));
        dispatch(setExport({ exportNameBase: tab.name }));
        dispatch(
          openExportDrawer({ showHistory: false, source: ExportSelectionSource.WIDGET_BUTTON })
        );
        break;
      }
      default:
        console.error('Unsupported export type');
    }
  };

  if (!isExportSupportedTab(tab)) {
    return null;
  }

  return (
    <ExportButton
      disabled={!isPermissionGranted}
      handleClick={handleClick}
      title='Export widget'
    />
  );
};

export default ExportWidgetButton;
